// Phase 4-D6 GREEN — s4Mv2P 工单更多操作 BottomSheet
//
// frame_id=s4Mv2P @ ui/dogdisk-user.pen modals.md §s4Mv2P(2026-05-21 补画 #8)
// 触发:`WjxGL moreBtn (e0CAy)` → 弹 BottomSheet
//
// 4 action(按 ticket.state 显隐):
//   - 转人工(ac1):仅 state='open' — POST /gapi/user/tickets/:id/transfer-to-human(D6 新 stub)
//   - 标记已解决(ac2):仅 state='open' / 'in_progress' — POST /gapi/user/tickets/:id/resolve
//   - 关闭工单(ac3):仅 state='resolved'(用户端只能 resolved → closed)— POST /gapi/user/tickets/:id/close
//   - 复制单号(ac4):始终显;navigator.clipboard.writeText(ticket_number)
//
// 边界:与 ticket-detail.tsx 既有 moreOpen 内嵌菜单并存(同位置,差异在样式 / 完整 action 集);
//   D6 GREEN 切换到本组件作 BottomSheet 体验(per .pen 设计稿)。
//
// data-testid 约定:
//   - 根:s4Mv2P
//   - 4 action:s4Mv2P-{transfer,resolve,close,copy-num}
//   - cancel:s4Mv2P-cancel
//
// toast 简化用 alert();后续可接 toast lib(本批次 P3 不引入新依赖)。

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiClient, ApiClientError, copyToClipboard } from '@dogdisk/shared'

export type TicketActionsSheetProps = {
  open: boolean
  onClose: () => void
  ticketId: string
  ticketNumber: string
  ticketState: string
  /** 操作成功后刷新 ticket(refetch get_ticket / list_messages)*/
  onActionDone?: () => void
}

type ActionKey = 'transfer' | 'resolve' | 'close' | 'copy-num'

export function TicketActionsSheet({
  open,
  onClose,
  ticketId,
  ticketNumber,
  ticketState,
  onActionDone,
}: TicketActionsSheetProps) {
  const [err, setErr] = useState<string | null>(null)

  const callAction = useMutation({
    mutationFn: async (key: ActionKey) => {
      switch (key) {
        case 'transfer':
          return apiClient.post(`/gapi/user/tickets/${ticketId}/transfer-to-human`, {})
        case 'resolve':
          return apiClient.post(`/gapi/user/tickets/${ticketId}/resolve`, {})
        case 'close':
          return apiClient.post(`/gapi/user/tickets/${ticketId}/close`, {})
        case 'copy-num':
          await copyToClipboard(ticketNumber)
          return { ok: true }
      }
    },
    onSuccess: (_data, key) => {
      setErr(null)
      // 关闭 sheet + refetch
      onClose()
      onActionDone?.()
      // 简化 toast — 后续接 toast lib
      if (key === 'transfer') {
        // eslint-disable-next-line no-alert -- D6 stub;后续接 toast
        window.alert('已转人工')
      } else if (key === 'resolve') {
        // eslint-disable-next-line no-alert
        window.alert('已标记为已解决')
      } else if (key === 'close') {
        // eslint-disable-next-line no-alert
        window.alert('工单已关闭')
      } else if (key === 'copy-num') {
        // eslint-disable-next-line no-alert
        window.alert(`已复制单号 ${ticketNumber}`)
      }
    },
    onError: (e: Error) => {
      if (e instanceof ApiClientError) {
        setErr(e.message || '操作失败')
      } else {
        setErr(e.message || '操作失败')
      }
    },
  })

  if (!open) return null

  // action 按 state 显隐
  const canTransfer = ticketState === 'open'
  const canResolve = ticketState === 'open' || ticketState === 'in_progress' || ticketState === 'pending'
  const canClose = ticketState === 'resolved'

  return (
    <div
      data-frame-id="s4Mv2P"
      data-testid="s4Mv2P"
      role="dialog"
      aria-modal="true"
      aria-label="工单更多操作"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-3">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>

        {err && <p className="px-6 pb-2 text-[12px] text-[#FF6B6B]">{err}</p>}

        <ul className="flex flex-col">
          {canTransfer && (
            <li>
              <button
                type="button"
                data-testid="s4Mv2P-transfer"
                disabled={callAction.isPending}
                onClick={() => callAction.mutate('transfer')}
                className="flex w-full flex-col items-start gap-0.5 border-b border-[#F3F4F6] px-6 py-3 text-left text-[14px] text-[#1A1A1A] active:bg-[#F6F7F8] disabled:opacity-50"
              >
                <span className="font-medium">转人工客服</span>
                <span className="text-[11px] text-[#9CA3AF]">当前自动回复中,转给真人处理</span>
              </button>
            </li>
          )}
          {canResolve && (
            <li>
              <button
                type="button"
                data-testid="s4Mv2P-resolve"
                disabled={callAction.isPending}
                onClick={() => callAction.mutate('resolve')}
                className="flex w-full flex-col items-start gap-0.5 border-b border-[#F3F4F6] px-6 py-3 text-left text-[14px] text-[#1A1A1A] active:bg-[#F6F7F8] disabled:opacity-50"
              >
                <span className="font-medium">标记已解决</span>
                <span className="text-[11px] text-[#9CA3AF]">客服会收到通知</span>
              </button>
            </li>
          )}
          {canClose && (
            <li>
              <button
                type="button"
                data-testid="s4Mv2P-close"
                disabled={callAction.isPending}
                onClick={() => callAction.mutate('close')}
                className="flex w-full flex-col items-start gap-0.5 border-b border-[#F3F4F6] px-6 py-3 text-left text-[14px] text-[#FF6B6B] active:bg-[#F6F7F8] disabled:opacity-50"
              >
                <span className="font-medium">关闭工单</span>
                <span className="text-[11px] text-[#9CA3AF]">关闭后不可再回复</span>
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              data-testid="s4Mv2P-copy-num"
              disabled={callAction.isPending}
              onClick={() => callAction.mutate('copy-num')}
              className="flex w-full flex-col items-start gap-0.5 px-6 py-3 text-left text-[14px] text-[#1A1A1A] active:bg-[#F6F7F8] disabled:opacity-50"
            >
              <span className="font-medium">复制工单号</span>
              <span className="font-mono text-[11px] text-[#9CA3AF]">{ticketNumber}</span>
            </button>
          </li>
        </ul>

        <div className="border-t border-[#F3F4F6] px-6 pt-3 pb-3">
          <button
            type="button"
            data-testid="s4Mv2P-cancel"
            onClick={onClose}
            className="h-11 w-full rounded-lg bg-[#F1F2F4] text-[14px] font-semibold text-[#1A1A1A]"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  )
}
